import React, { useEffect, useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { Modal } from '@/components/UI/Modal';
import { PageHeader } from '@/components/UI/PageHeader';
import { SearchInput } from '@/components/UI/SearchInput';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Switch } from '@/components/UI/Switch';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useListAgentsQuery,
  useSetAgentStatusMutation,
  useDeleteAgentMutation,
  useCloneAgentMutation,
  Agent,
} from '@/redux/internal-services/agentsApi';

// ── small sub-components ──────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, BadgeProps['variant']> = {
    active: 'success',
    disabled: 'neutral',
    deleted: 'danger',
    archived: 'warning',
  };
  return <Badge variant={map[status] ?? 'neutral'}>{status}</Badge>;
};

const AgentTypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const map: Record<string, BadgeProps['variant']> = {
    conversational: 'accent',
    workflow: 'warning',
  };
  const label: Record<string, string> = {
    conversational: 'Chat',
    workflow: 'Workflow',
  };
  return <Badge variant={map[type] ?? 'neutral'}>{label[type] ?? type}</Badge>;
};

/** Initials avatar fallback or icon_url avatar */
const AgentAvatar: React.FC<{ agent: Agent }> = ({ agent }) => {
  const initials = (agent.agent_name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  // Deterministic hue from the agent name for visual variety
  const hue = Array.from(agent.agent_name || '').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;

  if (agent.icon_url) {
    return (
      <img
        src={agent.icon_url}
        alt={agent.agent_name}
        className="h-8 w-8 rounded-full object-cover shrink-0"
      />
    );
  }

  return (
    <span
      className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 select-none"
      style={{ background: `hsl(${hue},60%,48%)` }}
    >
      {initials}
    </span>
  );
};

// ── sort helpers ──────────────────────────────────────────────────────────────

type SortKey = 'name-asc' | 'name-desc' | 'updated';

function sortAgents(agents: Agent[], key: SortKey): Agent[] {
  return [...agents].sort((a, b) => {
    if (key === 'name-asc') return a.agent_name.localeCompare(b.agent_name);
    if (key === 'name-desc') return b.agent_name.localeCompare(a.agent_name);
    if (key === 'updated') {
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    }
    return 0;
  });
}

// ── page ──────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const AgentsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name-asc');
  const [showArchived, setShowArchived] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Reset pagination whenever filters change
  useEffect(() => { setLimit(PAGE_SIZE); }, [search, statusFilter]);

  const { data, isLoading, refetch } = useListAgentsQuery({
    search: search || undefined,
    status: statusFilter || undefined,
    limit,
  });

  const hasMore = (data?.total ?? 0) > (data?.data?.length ?? 0);
  const [setStatus] = useSetAgentStatusMutation();
  const [deleteAgent] = useDeleteAgentMutation();
  const [cloneAgent] = useCloneAgentMutation();
  const [deleteConfirm, setDeleteConfirm] = useState<Agent | null>(null);
  const [cloneTarget, setCloneTarget] = useState<Agent | null>(null);
  const [cloneName, setCloneName] = useState('');

  const rawAgents = data?.data ?? [];

  // Filter out deleted/archived unless the user opts in
  const visibleAgents = useMemo(() => {
    const base = showArchived
      ? rawAgents
      : rawAgents.filter((a) => a.status !== 'deleted' && a.status !== 'archived');
    return sortAgents(base, sortKey);
  }, [rawAgents, showArchived, sortKey]);

  const hasArchivedAgents = rawAgents.some(
    (a) => a.status === 'deleted' || a.status === 'archived',
  );

  const toggle = async (a: Agent) => {
    await setStatus({
      id: a.agent_id,
      status: a.status === 'active' ? 'disabled' : 'active',
    })
      .unwrap()
      .catch(() => {});
    refetch();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteAgent(deleteConfirm.agent_id).unwrap().catch(() => {});
    setDeleteConfirm(null);
    refetch();
  };

  const openCloneDialog = (a: Agent) => {
    setCloneName(`${a.agent_name} (copy)`);
    setCloneTarget(a);
  };

  const handleClone = async () => {
    if (!cloneTarget) return;
    const name = cloneName.trim() || `${cloneTarget.agent_name} (copy)`;
    await cloneAgent({ id: cloneTarget.agent_id, agent_name: name }).unwrap().catch(() => {});
    setCloneTarget(null);
    setCloneName('');
    refetch();
  };

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <PageHeader
          title="AI Agents"
          subtitle="Create and configure SAM agents available to tenants."
          actions={
            <Button onClick={() => navigate('/platform/agents/new')}>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Agent
            </Button>
          }
          toolbar={
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agents..."
                wrapperClassName="min-w-[180px] flex-1"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={fieldClass({ size: 'sm' }) + ' !w-auto'}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className={fieldClass({ size: 'sm' }) + ' !w-auto'}
              >
                <option value="name-asc">Name A–Z</option>
                <option value="name-desc">Name Z–A</option>
                <option value="updated">Last Updated</option>
              </select>
              {(hasArchivedAgents || showArchived) && (
                <div className="flex items-center gap-2 text-sm text-ui-text2">
                  <Switch
                    checked={showArchived}
                    onCheckedChange={setShowArchived}
                    aria-label="Show archived"
                  />
                  Show archived
                </div>
              )}
            </div>
          }
        />
      }
    >

      {/* body */}
      {isLoading ? (
        <LoadingState label="Loading agents…" />
      ) : visibleAgents.length === 0 && rawAgents.length === 0 ? (
        /* ── true empty state ── */
        <EmptyState
          title={t('platform.noAgentsYet')}
          description={t('platform.noAgentsYetDescription')}
          action={
            <Button onClick={() => navigate('/platform/agents/new')}>
              {t('platform.createFirstAgent')}
            </Button>
          }
        />
      ) : visibleAgents.length === 0 ? (
        /* ── filtered to zero ── */
        <EmptyState variant="inline" title={t('platform.noAgentsMatchFilters')} />
      ) : (
        <div className="space-y-3">
          {visibleAgents.map((a) => (

            <div
              key={a.agent_id}
              onClick={() => navigate(`/platform/agents/${a.agent_id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate(`/platform/agents/${a.agent_id}`);
              }}
              className={`card-surface flex cursor-pointer items-start justify-between gap-4 border-l-4 p-5 transition-shadow hover:shadow-pop ${a.status === 'active' ? 'border-l-success' : a.status === 'archived' || a.status === 'deleted' ? 'border-l-danger' : 'border-l-ui-border3'}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <AgentAvatar agent={a} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-ui-text truncate">{a.agent_name}</h3>
                    <StatusPill status={a.status} />
                    <AgentTypeBadge type={a.agent_type} />
                    <Badge variant="neutral">{a.models?.text}</Badge>
                    {a.tenant_id == null && <Badge variant="accent">Global</Badge>}
                  </div>
                  <p className="text-sm text-ui-text2 mt-1 line-clamp-2">{a.description || '—'}</p>
                  <p
                    className="mt-0.5 cursor-copy font-mono text-xs text-ui-text3 hover:text-ui-text2"
                    title="Click to copy"
                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(a.agent_id); }}
                  >{a.agent_id}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="xs" onClick={() => toggle(a)}>
                  {a.status === 'active' ? 'Disable' : 'Enable'}
                </Button>
                <Button variant="soft" size="xs" onClick={() => navigate(`/platform/agents/${a.agent_id}`)}>
                  Edit
                </Button>
                <Button variant="outline" size="xs" onClick={() => openCloneDialog(a)}>
                  Clone
                </Button>
                <Button variant="destructive-ghost" size="xs" onClick={() => setDeleteConfirm(a)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="pt-2 flex justify-center">
              <Button variant="soft" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                Load more ({(data?.total ?? 0) - (data?.data?.length ?? 0)} remaining)
              </Button>
            </div>
          )}
        </div>
      )}

      {/* clone name dialog */}
      {cloneTarget && (
        <Modal
          title="Clone Agent"
          onClose={() => { setCloneTarget(null); setCloneName(''); }}
          onSave={handleClone}
          saveLabel="Clone"
          maxWidthClassName="max-w-sm"
          closeOnBackdrop={false}
        >
          <p className="text-sm text-ui-text2">Choose a name for the cloned agent.</p>
          <input
            autoFocus
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleClone()}
            className={fieldClass()}
          />
        </Modal>
      )}

      {/* delete confirmation modal */}
      {deleteConfirm && (
        <Modal
          title="Delete Agent"
          onClose={() => setDeleteConfirm(null)}
          onSave={handleDelete}
          saveLabel="Delete"
          danger
          maxWidthClassName="max-w-sm"
        >
          <p className="text-sm text-ui-text2">
            Delete <strong>{deleteConfirm.agent_name}</strong>? This cannot be undone.
          </p>
        </Modal>
      )}
    </PageShell>
  );
};

export default AgentsPage;
